/**
 * ElectronService - Clean wrapper with explicit method mapping
 * Automatically detects and uses appropriate async/sync bridge methods
 */

import { logger } from '@services/logger/logger.service';
import { getErrorCode } from '@services/logger/scrubFilePaths';

type PathName = 'home' | 'appData' | 'userData';

interface BridgeMethodPair {
  sync?: string;
  async?: string;
}

class ElectronService {
  private bridgeVersion: string | null = null;
  private isLegacyBridge = false;

  private readonly methodMap: Record<string, BridgeMethodPair> = {
    getArch: { sync: 'getArch', async: 'getArchAsync' },
    getHomedir: { sync: 'getHomedir', async: 'getHomedirAsync' },
    getPlatform: { sync: 'getPlatform', async: 'getPlatformAsync' },
    getOsType: { sync: 'getOsType', async: 'getOsTypeAsync' },
    getOsRelease: { sync: 'getOsRelease', async: 'getOsReleaseAsync' },
    getElectronVersion: { sync: 'getElectronVersion', async: 'getElectronVersionAsync' },
    readFileInWatchPath: { sync: 'readFileInWatchPath', async: 'readFileInWatchPathAsync' },
    joinPaths: { sync: 'joinPaths', async: 'joinPathsAsync' },
    getBaseName: { sync: 'getBaseName', async: 'getBaseNameAsync' },
    getAllFilePathsFromDir: {
      sync: 'getAllFilePathsFromDir',
      async: 'getAllFilePathsFromDirAsync',
    },
    getPath: { sync: 'getPath', async: 'getPathAsync' },
    ensureVirtualPrinterLink: { async: 'ensureVirtualPrinterLink' },
    selectDirectory: { sync: 'selectDirectory', async: 'selectDirectoryAsync' },
    isExecPathDirectory: { sync: 'isExecPathDirectory', async: 'isExecPathDirectoryAsync' },
    initFileWatcher: { sync: 'initFileWatcher', async: 'initFileWatcherAsync' },
    statFile: { sync: 'statFile', async: 'statFileAsync' },
    createNotificationWindow: {
      sync: 'createNotificationWindow',
      async: 'createNotificationWindowAsync',
    },
    createSettingsWindow: { sync: 'createSettingsWindow', async: 'createSettingsWindowAsync' },
  };

  private async callMethod<T>(methodKey: string, args: any[] = []): Promise<T> {
    const methods = this.methodMap[methodKey];
    if (!methods) {
      throw new Error(`Unknown method: ${methodKey}`);
    }

    const electronBridge = window.electron as any;

    try {
      // Try async first if not legacy
      if (!this.isLegacyBridge && methods.async && electronBridge?.[methods.async]) {
        return await electronBridge[methods.async](...args);
      }

      // Fallback to sync
      if (methods.sync && electronBridge?.[methods.sync]) {
        return electronBridge[methods.sync](...args);
      }

      throw new Error(`No ${methodKey} method available`);
    } catch (error) {
      logger.error(
        'DESKTOP',
        `Failed to call ${methodKey} (${getErrorCode(error)})`,
        undefined,
        false,
      );
      throw error;
    }
  }

  private setupWindowFinishLoadListener(
    windowType: string,
    result: any,
    onDidFinishLoad: () => void,
  ): void {
    if (!onDidFinishLoad || typeof onDidFinishLoad !== 'function' || !result?.id) {
      return;
    }

    const eventName = `${windowType}-window-${result.id}-did-finish-load`;

    const handleFinishLoad = () => {
      logger.debug('DESKTOP', `${windowType} window finished loading, calling callback`);
      onDidFinishLoad();
      window.electron?.getIpcRenderer?.()?.removeListener(eventName, handleFinishLoad);
    };

    window.electron?.getIpcRenderer?.()?.on(eventName, handleFinishLoad);
  }

  async detectBridgeVersion(): Promise<string> {
    if (this.bridgeVersion) {
      return this.bridgeVersion;
    }

    logger.debug('DESKTOP', 'Detecting bridge version...');

    if (window.electron?.getElectronVersion) {
      this.bridgeVersion = window.electron.getElectronVersion();
      this.isLegacyBridge = true;
      logger.debug('DESKTOP', 'Bridge version detected as legacy', {
        bridgeVersion: this.bridgeVersion,
      });
      return this.bridgeVersion;
    }

    if (window.electron?.getElectronVersionAsync) {
      this.bridgeVersion = await window.electron.getElectronVersionAsync();
      this.isLegacyBridge = false;
      logger.debug('DESKTOP', 'Bridge version detected as modern', {
        bridgeVersion: this.bridgeVersion,
      });
      return this.bridgeVersion;
    }

    throw new Error('No electron version method available');
  }

  async getArch(): Promise<string> {
    return await this.callMethod<string>('getArch');
  }

  /**
   * Lit un fichier depuis le répertoire de surveillance
   */
  async readFileInWatchPath(fileName: string): Promise<Buffer> {
    if (!fileName || typeof fileName !== 'string') {
      throw new Error('fileName must be a non-empty string');
    }

    return await this.callMethod<Buffer>('readFileInWatchPath', [fileName]);
  }

  async getHomedir(): Promise<string> {
    return await this.callMethod<string>('getHomedir');
  }

  async joinPaths(...paths: string[]): Promise<string> {
    return await this.callMethod<string>('joinPaths', paths);
  }

  async getPlatform(): Promise<string> {
    return await this.callMethod<string>('getPlatform');
  }

  async getOsType(): Promise<string> {
    return await this.callMethod<string>('getOsType');
  }

  async getOsRelease(): Promise<string> {
    return await this.callMethod<string>('getOsRelease');
  }

  async getElectronVersion(): Promise<string> {
    return await this.callMethod<string>('getElectronVersion');
  }

  async ensureVirtualPrinterLink(folderName: string): Promise<boolean> {
    return await this.callMethod<boolean>('ensureVirtualPrinterLink', [folderName]);
  }

  async getBaseName(filePath: string): Promise<string> {
    return await this.callMethod<string>('getBaseName', [filePath]);
  }

  async getAllFilePathsFromDir(dirPath: string): Promise<string[]> {
    return await this.callMethod<string[]>('getAllFilePathsFromDir', [dirPath]);
  }

  async getPath(name: PathName): Promise<string> {
    return await this.callMethod<string>('getPath', [name]);
  }

  async initFileWatcher(options?: any): Promise<any> {
    logger.debug('DESKTOP', 'ElectronService.initFileWatcher called');
    try {
      const result = await this.callMethod<any>('initFileWatcher', [options]);
      logger.debug(
        'DESKTOP',
        `ElectronService.initFileWatcher result - type: ${typeof result}, has .on: ${typeof result?.on}`,
      );
      return result;
    } catch (error) {
      logger.error(
        'DESKTOP',
        `ElectronService.initFileWatcher failed (${getErrorCode(error)})`,
        undefined,
        false,
      );
      throw error;
    }
  }

  async statFile(filePath: string): Promise<any> {
    const stats = await this.callMethod<any>('statFile', [filePath]);

    // Normalize the interface - old bridge returns methods, new bridge returns properties
    if (typeof stats.isFile === 'function') {
      // Old bridge - convert methods to properties
      return {
        isFile: stats.isFile(),
        isDirectory: stats.isDirectory(),
        size: stats.size,
        mtime: stats.mtime,
      };
    } else {
      // New bridge - already has properties
      return stats;
    }
  }

  async createNotificationWindow(url: string, options: Record<string, any> = {}): Promise<any> {
    try {
      const { onDidFinishLoad, ...serializableOptions } = options;
      const result = await this.callMethod<any>('createNotificationWindow', [
        url,
        serializableOptions,
      ]);

      // If there's a callback, set up an IPC listener for the did-finish-load event
      this.setupWindowFinishLoadListener('notification', result, onDidFinishLoad);

      return result;
    } catch (error) {
      logger.error(
        'DESKTOP',
        `Failed to create notification window (${getErrorCode(error)})`,
        undefined,
        false,
      );
      throw error;
    }
  }

  /**
   * Create a settings window
   */
  async createSettingsWindow(url: string, options: Record<string, any> = {}): Promise<any> {
    try {
      const { onDidFinishLoad, ...serializableOptions } = options;
      const result = await this.callMethod<any>('createSettingsWindow', [url, serializableOptions]);

      // If there's a callback, set up an IPC listener for the did-finish-load event
      this.setupWindowFinishLoadListener('settings', result, onDidFinishLoad);

      return result;
    } catch (error) {
      logger.error(
        'DESKTOP',
        `Failed to create settings window (${getErrorCode(error)})`,
        undefined,
        false,
      );
      throw error;
    }
  }

  /**
   * Opens a directory selection dialog
   */
  async selectDirectory(options: any = {}): Promise<string[] | undefined> {
    return await this.callMethod<string[] | undefined>('selectDirectory', [options]);
  }

  /**
   * Checks if a directory path is the executable path directory
   */
  async isExecPathDirectory(dirPath: string): Promise<boolean> {
    return await this.callMethod<boolean>('isExecPathDirectory', [dirPath]);
  }

  isElectronEnvironment(): boolean {
    return typeof window !== 'undefined' && window.electron !== undefined;
  }

  /**
   * Logout handling for both old and new Electron versions
   */
  async logout(logoutUrl: string): Promise<void> {
    if (!this.isElectronEnvironment()) {
      throw new Error('Not in electron environment');
    }

    const electronVersion = await this.getElectronVersion();
    const isNewElectron = electronVersion.startsWith('200.');

    if (isNewElectron) {
      if (window.electron?.logout) {
        return window.electron.logout();
      }
      throw new Error('Logout method not available in new Electron version');
    } else {
      if (window.electron?.redirectTo) {
        return window.electron.redirectTo(logoutUrl);
      }
      throw new Error('RedirectTo method not available in old Electron version');
    }
  }
}

// Export singleton
export const electronService = new ElectronService();
export default electronService;
